/**
 * gammaScoring.ts — MER Algo Footprints v7.1B — Phase 6
 *
 * Proxy-based gamma regime classifier.
 * Pure function — no side effects, no external calls.
 *
 * All scores are bounded to [0, 100] or [-100, +100] as documented.
 * Formulas are explainable and conservative. No overfitting.
 *
 * This does NOT produce dealer-exact GEX. It produces a practical
 * regime signal based on OI structure and concentration patterns.
 *
 * Placement: src/domains/market/engines/gamma/gammaScoring.ts
 */

import type {
  NormalizedOptionContract,
  StrikeLevel,
  GammaSnapshot,
  GammaRegimeV2,
  GammaBias,
  TradeBehaviorProfile,
} from './gammaTypes';
import { computeGammaDataCoverage } from './optionsChainNormalizer';

// ─── Named thresholds ─────────────────────────────────────────────────────────

const NEAR_MONEY_PCT      =  5.0;   // ±5% from spot = near-money
const ATM_BAND_PCT        =  0.5;   // ±0.5% = ATM
const FRONT_EXPIRY_DTE    =  2;     // DTE ≤ this = front-expiry (high pin risk)
const PIN_SCORE_THRESHOLD = 60;     // pinScore above this → PIN bias candidate
const EXPANSION_THRESHOLD = 55;     // expansionScore above this → EXPANSION bias
const POSITIVE_GAMMA_THRESHOLD = 55;  // gammaScore above this → POSITIVE regime
const NEGATIVE_GAMMA_THRESHOLD = 40;  // gammaScore below this → NEGATIVE regime
const MIN_STRIKES_FOR_CONFIDENCE = 5; // fewer strikes → low confidence
const LOW_COVERAGE_CONFIDENCE_ADJ = -15; // penalty when gamma data is sparse

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function bound100(val: number): number {
  return clamp(val, 0, 100);
}

// ─── Sub-scores ───────────────────────────────────────────────────────────────

/**
 * ATM concentration score (0–100).
 * High score = a lot of OI is pinned right around spot → pinning regime likely.
 */
function scoreATMConcentration(levels: StrikeLevel[], spot: number): number {
  const total = levels.reduce((s, l) => s + l.totalOI, 0);
  if (total === 0) return 0;
  const atmOI = levels
    .filter(l => Math.abs(l.distFromSpotPct) <= ATM_BAND_PCT)
    .reduce((s, l) => s + l.totalOI, 0);
  return bound100((atmOI / total) * 200); // ×200 because ATM band is narrow
}

/**
 * Near-money density score (0–100).
 * High = significant OI in ±5% band → short-gamma if dispersed, long-gamma if concentrated.
 */
function scoreNearMoneyDensity(levels: StrikeLevel[], spot: number): number {
  const total = levels.reduce((s, l) => s + l.totalOI, 0);
  if (total === 0) return 0;
  const nearOI = levels
    .filter(l => Math.abs(l.distFromSpotPct) <= NEAR_MONEY_PCT)
    .reduce((s, l) => s + l.totalOI, 0);
  return bound100((nearOI / total) * 100);
}

/**
 * Front-expiry intensity score (0–100).
 * High = heavy OI in contracts expiring very soon → extreme pin risk.
 */
function scoreFrontExpiryIntensity(chain: NormalizedOptionContract[]): number {
  const total = chain.reduce((s, c) => s + c.openInterest, 0);
  if (total === 0) return 0;
  const frontOI = chain
    .filter(c => c.dte <= FRONT_EXPIRY_DTE)
    .reduce((s, c) => s + c.openInterest, 0);
  return bound100((frontOI / total) * 100);
}

/**
 * Directional pressure score (-100 to +100).
 * Positive = call pressure dominant (upside resistance / positive gamma above).
 * Negative = put pressure dominant (downside support / negative gamma below).
 */
function scoreDirectionalPressure(levels: StrikeLevel[], spot: number): number {
  let callOIAbove = 0;
  let putOIBelow  = 0;
  let totalOI     = 0;

  for (const l of levels) {
    totalOI += l.totalOI;
    if (l.strike > spot) callOIAbove += l.callOI;
    if (l.strike < spot) putOIBelow  += l.putOI;
  }
  if (totalOI === 0) return 0;

  // Net directional: positive = more call OI above (dealers long gamma → mean revert)
  const net = (callOIAbove - putOIBelow) / totalOI;
  return clamp(Math.round(net * 100), -100, 100);
}

/**
 * Cluster containment score (0–100).
 * Measures how tightly OI is contained in a narrow strike range.
 * High containment → positive gamma / pinning behavior.
 */
function scoreClusterContainment(levels: StrikeLevel[], spot: number): number {
  if (levels.length < 2) return 0;
  const total = levels.reduce((s, l) => s + l.totalOI, 0);
  if (total === 0) return 0;
  // Weighted standard deviation of strike distance from spot
  let wSum = 0;
  let wSumSq = 0;
  for (const l of levels) {
    const w = l.totalOI / total;
    wSum   += w * Math.abs(l.distFromSpotPct);
    wSumSq += w * l.distFromSpotPct * l.distFromSpotPct;
  }
  const wSD = Math.sqrt(Math.max(0, wSumSq - wSum * wSum));
  // Tight cluster = low wSD. Map: wSD 0 → 100, wSD 20 → 0
  return bound100(100 - (wSD / 20) * 100);
}

// ─── Key strike detection ─────────────────────────────────────────────────────

function findCallWall(levels: StrikeLevel[], spot: number): number | null {
  const above = levels.filter(l => l.strike > spot);
  if (!above.length) return null;
  return above.reduce((best, l) => l.callOI > best.callOI ? l : best, above[0]).strike;
}

function findPutWall(levels: StrikeLevel[], spot: number): number | null {
  const below = levels.filter(l => l.strike < spot);
  if (!below.length) return null;
  return below.reduce((best, l) => l.putOI > best.putOI ? l : best, below[0]).strike;
}

function findKeyMagnet(levels: StrikeLevel[]): number | null {
  if (!levels.length) return null;
  return levels.reduce((best, l) => l.magnetScore > best.magnetScore ? l : best, levels[0]).strike;
}

/**
 * Flip zone: region where call OI ≈ put OI (gamma sign-change proxy).
 * Returns [low, high] bracket or null if no clear balance point exists.
 */
function findFlipZone(levels: StrikeLevel[], spot: number): [number, number] | null {
  // Find strike where |callOI - putOI| / totalOI is minimized
  let minImbalance = Infinity;
  let flipStrike: number | null = null;

  for (const l of levels) {
    if (l.totalOI === 0) continue;
    const imbalance = Math.abs(l.callOI - l.putOI) / l.totalOI;
    if (imbalance < minImbalance) {
      minImbalance = imbalance;
      flipStrike = l.strike;
    }
  }
  if (flipStrike === null || minImbalance > 0.3) return null; // no clear flip point

  // Bracket with adjacent strikes (or ±0.5%)
  const idx = levels.findIndex(l => l.strike === flipStrike);
  const prev = idx > 0 ? levels[idx - 1].strike : flipStrike * 0.995;
  const next = idx < levels.length - 1 ? levels[idx + 1].strike : flipStrike * 1.005;
  return [prev, next];
}

/**
 * Breakout fuel: normalized OI accumulation beyond the call/put walls.
 * High fuel above → if call wall breaks, dealers must chase aggressively.
 */
function computeBreakoutFuel(
  levels: StrikeLevel[],
  callWall: number | null,
  putWall: number | null,
): { up: number; down: number } {
  const total = levels.reduce((s, l) => s + l.totalOI, 0);
  if (total === 0 || (!callWall && !putWall)) return { up: 0, down: 0 };

  const upOI   = callWall ? levels.filter(l => l.strike > callWall).reduce((s, l) => s + l.callOI, 0) : 0;
  const downOI = putWall  ? levels.filter(l => l.strike < putWall).reduce((s, l) => s + l.putOI,  0) : 0;

  return {
    up:   clamp(upOI   / total, 0, 1),
    down: clamp(downOI / total, 0, 1),
  };
}

// ─── Trade behavior profile ───────────────────────────────────────────────────

function buildBehaviorProfile(
  regime: GammaRegimeV2,
  bias: GammaBias,
  pinScore: number,
  expansionScore: number,
  directionalScore: number,
): TradeBehaviorProfile {
  if (regime === 'POSITIVE') {
    if (bias === 'PIN') {
      return {
        expectedVolatility:  'compressed',
        breakoutLikelihood:  'low',
        reversionLikelihood: 'high',
        driftBias:           'neutral',
        hedgingPressure:     'selling',
        note: 'Positive gamma pinning — dealers sell rallies and buy dips; expect range compression near key strikes.',
      };
    }
    return {
      expectedVolatility:  'normal',
      breakoutLikelihood:  'moderate',
      reversionLikelihood: 'moderate',
      driftBias:           directionalScore > 20 ? 'upward' : directionalScore < -20 ? 'downward' : 'neutral',
      hedgingPressure:     'selling',
      note: 'Positive gamma balanced — dealer hedging dampens volatility; directional bias may develop.',
    };
  }

  if (regime === 'NEGATIVE') {
    if (bias === 'UPSIDE_EXPANSION') {
      return {
        expectedVolatility:  'elevated',
        breakoutLikelihood:  'high',
        reversionLikelihood: 'low',
        driftBias:           'upward',
        hedgingPressure:     'buying',
        note: 'Negative gamma upside — dealers buy as price rises, amplifying moves above key strikes.',
      };
    }
    if (bias === 'DOWNSIDE_EXPANSION') {
      return {
        expectedVolatility:  'elevated',
        breakoutLikelihood:  'high',
        reversionLikelihood: 'low',
        driftBias:           'downward',
        hedgingPressure:     'selling',
        note: 'Negative gamma downside — dealers sell as price falls, amplifying moves below key strikes.',
      };
    }
    return {
      expectedVolatility:  'explosive',
      breakoutLikelihood:  'high',
      reversionLikelihood: 'low',
      driftBias:           'neutral',
      hedgingPressure:     'buying',
      note: 'Negative gamma balanced — market prone to sharp moves in either direction; avoid fading breakouts.',
    };
  }

  // NEUTRAL
  return {
    expectedVolatility:  'normal',
    breakoutLikelihood:  'moderate',
    reversionLikelihood: 'moderate',
    driftBias:           'neutral',
    hedgingPressure:     'neutral',
    note: 'Neutral gamma — no dominant regime; watch for dealer positioning to resolve directional pressure.',
  };
}

// ─── Main scoring function ────────────────────────────────────────────────────

export interface GammaScoringInput {
  symbol:      string;
  spot:        number;
  chain:       NormalizedOptionContract[];
  levels:      StrikeLevel[];
  maxDTE:      number;
  computedAt:  number;
}

export function scoreGamma(input: GammaScoringInput): GammaSnapshot {
  const { symbol, spot, chain, levels, maxDTE, computedAt } = input;
  const notes: string[] = [];

  // ── Sub-scores ──────────────────────────────────────────────────────────────
  const atmConc       = scoreATMConcentration(levels, spot);
  const nearMoneyDens = scoreNearMoneyDensity(levels, spot);
  const frontExpiry   = scoreFrontExpiryIntensity(chain);
  const directional   = scoreDirectionalPressure(levels, spot);
  const clusterCont   = scoreClusterContainment(levels, spot);

  // ── Pin score (0–100) ────────────────────────────────────────────────────────
  // Strong ATM concentration + front expiry + tight cluster = high pin probability
  const pinScore = bound100(
    atmConc * 0.40 +
    frontExpiry * 0.35 +
    clusterCont * 0.25
  );

  // ── Expansion score (0–100) ──────────────────────────────────────────────────
  // Low containment + dispersed near-money OI = short-gamma expansion environment
  const expansionScore = bound100(
    (100 - clusterCont) * 0.50 +
    (100 - atmConc)     * 0.30 +
    nearMoneyDens       * 0.20
  );

  // ── Gamma score (0–100) ──────────────────────────────────────────────────────
  // High = positive gamma (pinning); Low = negative gamma (expansion)
  const gammaScore = bound100(
    clusterCont  * 0.35 +
    atmConc      * 0.30 +
    nearMoneyDens * 0.20 +
    (pinScore > expansionScore ? 15 : 0)
  );

  // ── Directional score (-100 to +100) ────────────────────────────────────────
  const directionalScore = directional; // already in [-100, 100]

  // ── Key strikes ─────────────────────────────────────────────────────────────
  const callWall        = findCallWall(levels, spot);
  const putWall         = findPutWall(levels, spot);
  const keyMagnetStrike = findKeyMagnet(levels);
  const flipZone        = findFlipZone(levels, spot);
  const { up: breakoutFuelUp, down: breakoutFuelDown } = computeBreakoutFuel(levels, callWall, putWall);

  // ── Regime classification ────────────────────────────────────────────────────
  let regime: GammaRegimeV2;
  if (gammaScore >= POSITIVE_GAMMA_THRESHOLD) {
    regime = 'POSITIVE';
  } else if (gammaScore <= NEGATIVE_GAMMA_THRESHOLD) {
    regime = 'NEGATIVE';
  } else {
    regime = 'NEUTRAL';
  }

  // ── Bias classification ──────────────────────────────────────────────────────
  let bias: GammaBias;
  if (pinScore >= PIN_SCORE_THRESHOLD && regime === 'POSITIVE') {
    bias = 'PIN';
  } else if (expansionScore >= EXPANSION_THRESHOLD && regime === 'NEGATIVE') {
    if (directionalScore > 10) {
      bias = 'UPSIDE_EXPANSION';
    } else if (directionalScore < -10) {
      bias = 'DOWNSIDE_EXPANSION';
    } else {
      bias = 'BALANCED';
    }
  } else {
    bias = 'BALANCED';
  }

  // ── Confidence adjustment ────────────────────────────────────────────────────
  const gammaCoverage = computeGammaDataCoverage(chain);
  let confidenceAdjustment = 0;

  if (levels.length < MIN_STRIKES_FOR_CONFIDENCE) {
    confidenceAdjustment -= 15;
    notes.push(`Limited strike coverage (${levels.length} strikes) — regime confidence reduced.`);
  }
  if (gammaCoverage < 0.3) {
    confidenceAdjustment += LOW_COVERAGE_CONFIDENCE_ADJ;
    notes.push(`Low gamma data coverage (${Math.round(gammaCoverage * 100)}%) — using OI proxy only.`);
  }
  if (chain.length < 10) {
    confidenceAdjustment -= 10;
    notes.push('Sparse options chain — results are indicative only.');
  }
  if (frontExpiry > 70) {
    notes.push('Heavy front-expiry concentration — pin risk elevated today.');
  }
  if (breakoutFuelUp > 0.3) {
    notes.push(`Significant call OI above call wall (fuel=${(breakoutFuelUp * 100).toFixed(0)}%) — breakout above could accelerate.`);
  }
  if (breakoutFuelDown > 0.3) {
    notes.push(`Significant put OI below put wall (fuel=${(breakoutFuelDown * 100).toFixed(0)}%) — breakdown below could accelerate.`);
  }

  confidenceAdjustment = clamp(confidenceAdjustment, -20, 20);

  // ── Data quality ─────────────────────────────────────────────────────────────
  const dataQuality: GammaSnapshot['dataQuality'] =
    gammaCoverage >= 0.6 && levels.length >= 10 ? 'high' :
    gammaCoverage >= 0.3 || levels.length >= MIN_STRIKES_FOR_CONFIDENCE ? 'medium' : 'low';

  // ── Behavior profile ─────────────────────────────────────────────────────────
  const tradeBehaviorProfile = buildBehaviorProfile(
    regime, bias, pinScore, expansionScore, directionalScore
  );

  return {
    symbol,
    spot,
    computedAt,
    dataQuality,
    regime,
    bias,
    gammaScore:       Math.round(gammaScore),
    pinScore:         Math.round(pinScore),
    expansionScore:   Math.round(expansionScore),
    directionalScore: Math.round(directionalScore),
    callWall,
    putWall,
    keyMagnetStrike,
    flipZoneLow:      flipZone ? flipZone[0] : null,
    flipZoneHigh:     flipZone ? flipZone[1] : null,
    breakoutFuelUp:   Math.round(breakoutFuelUp * 100) / 100,
    breakoutFuelDown: Math.round(breakoutFuelDown * 100) / 100,
    confidenceAdjustment,
    strikesAnalyzed:  levels.length,
    contractsAnalyzed: chain.length,
    maxDTE,
    tradeBehaviorProfile,
    notes,
  };
}
